import * as React from 'react'

import { inject, observer } from 'mobx-react'

import {
  IAttachment,
  IChecklistAnswerItem,
  IFormMaterial,
  IFormMaterialResource,
  IMeasure,
  IPermitTypeField,
  IRichText,
  ISignature,
  ISiteLocation,
  PermitFieldType,
} from '~/client/graph/types.generated'
import CompanyProfilePreview from '~/client/src/shared/components/CompanyProfilePreview/CompanyProfilePreview'
import RichEditorParsed from '~/client/src/shared/components/RichTextEditor/components/RichEditorParsed'
import { Toggle } from '~/client/src/shared/components/shadcn-ui/toggle'
import WorkflowFormFieldViewMode from '~/client/src/shared/components/SitePermitCreationForm/components/PermitFormFields/components/WorkflowFormFieldViewMode'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import FileAttachment from '~/client/src/shared/components/WorkflowCard/FileAttachment'
import { equipmentPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import QuestionnaireType from '~/client/src/shared/enums/QuestionnaireType'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { getDeletedObjectLabel } from '~/client/src/shared/utils/getDeletedObjectLabel'
import {
  getBullet,
  getQuestionnaireOptionsByType,
} from '~/client/src/shared/utils/PermitInstructionsHelper'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import SignaturePreview from '../SitePermitCreationForm/components/PermitFormFields/components/SignaturePreview'

export interface IReadonlyFieldStores {
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  projectDateStore?: ProjectDateStore
}

interface IProps extends IReadonlyFieldStores {
  value: unknown
  typeField: IPermitTypeField
}

interface IReadonlyFieldRowProps extends IReadonlyFieldStores {
  values: unknown[]
  typeField: IPermitTypeField
  showHeader?: boolean
}

const MaterialResourceLabel = inject('state')(
  observer(
    ({ locationId, state }: { locationId: string; state?: InitialState }) => {
      const projectId = state?.activeProject?.id
      // Deleted Materials included, so a saved workflow keeps naming its pick.
      const { recordsWithDeletedItems, isReady } = useMaterialResources({
        projectId,
      })
      const record = recordsWithDeletedItems.find(
        r => String(r.id) === String(locationId),
      )
      const name = record
        ? record.stxResourceId
          ? `${record.stxResourceId}: ${record.name}`
          : record.name
        : null
      const label = record
        ? record.isDeleted
          ? getDeletedObjectLabel(name)
          : name
        : isReady
          ? getDeletedObjectLabel()
          : locationId
      return <div className="text large word-break-all">{label}</div>
    },
  ),
)

@inject(
  'projectMembersStore',
  'companiesStore',
  'locationAttributesStore',
  'materialsStore',
  'projectDateStore',
)
@observer
export class ReadonlyFieldValue extends React.Component<IProps> {
  public render() {
    const { value, typeField } = this.props

    if (value === null || value === undefined) {
      return <div className="text large word-break-all">{NO_VALUE}</div>
    }

    return this.renderByType(value, typeField)
  }

  private renderByType(
    value: unknown,
    typeField: IPermitTypeField,
  ): JSX.Element {
    const {
      projectMembersStore,
      companiesStore,
      locationAttributesStore,
      materialsStore,
      projectDateStore,
    } = this.props

    switch (typeField.type) {
      case PermitFieldType.Signature:
        return (
          <SignaturePreview
            signature={value as ISignature}
            isUserStampEnabled={typeField.isUserStampEnabled}
          />
        )

      case PermitFieldType.File: {
        const attachment = value as IAttachment
        return (
          <FileAttachment
            attachment={attachment}
            className="iconless-row permit-form-attachment"
          />
        )
      }

      case PermitFieldType.Assignee:
      case PermitFieldType.Requester:
      case PermitFieldType.Subscriber:
      case PermitFieldType.User: {
        const userId = value as string
        const user = projectMembersStore?.getById(userId)
        if (!user) {
          return <div className="text large word-break-all">{NO_VALUE}</div>
        }
        return <UserProfilePreview user={user} />
      }

      case PermitFieldType.Company:
      case PermitFieldType.CompanyInformational: {
        const companyId = value as string
        const company = companiesStore?.getCompanyById(companyId)
        if (!company) {
          return <div className="text large word-break-all">{NO_VALUE}</div>
        }
        return (
          <CompanyProfilePreview
            company={company}
            isDeactivatedShown
            isMenuHidden
            isPhoneHidden
          />
        )
      }

      case PermitFieldType.Location:
      case PermitFieldType.Equipment:
      case PermitFieldType.LocationInformational:
      case PermitFieldType.EquipmentInformational: {
        const loc = value as ISiteLocation
        const isEquipment = equipmentPermitFieldTypes.includes(typeField.type)
        // Equipment deleted after the step was submitted stays named, marked.
        const location = isEquipment
          ? locationAttributesStore?.allItemsMapWithDeleted[loc?.id]
          : locationAttributesStore?.getById(loc?.id)
        if (!location?.id) {
          // Only call it unresolvable once equipment has loaded; until then an
          // empty map means "not hydrated", not "deleted".
          const isUnresolvable =
            isEquipment &&
            !!loc?.id &&
            !!locationAttributesStore?.offloadingEquipmentsStore?.isDataReceived
          return (
            <div className="text large word-break-all">
              {isUnresolvable ? getDeletedObjectLabel() : NO_VALUE}
            </div>
          )
        }
        return (
          <div className="text large">
            {location.isDeleted
              ? getDeletedObjectLabel(location.name)
              : location.name}
          </div>
        )
      }

      case PermitFieldType.MaterialResource:
      case PermitFieldType.MaterialResourceInformational: {
        const mr = value as IFormMaterialResource
        if (!mr?.locationId) {
          return <div className="text large word-break-all">{NO_VALUE}</div>
        }
        return <MaterialResourceLabel locationId={mr.locationId} />
      }

      case PermitFieldType.Material:
      case PermitFieldType.MaterialInformational: {
        const mat = value as IFormMaterial
        const materials = materialsStore?.getByIds([mat?.materialId])
        const material = materials?.[0]
        return (
          <>
            <div className="text large word-break-all">
              {material?.productName || NO_VALUE}
            </div>
            {!!material &&
              material.includesProcurementId(mat.procurementId) && (
                <>
                  <div className="text large grey word-break-all py4">
                    {Localization.translator.procurementID}
                  </div>
                  <div className="text large word-break-all">
                    {mat.procurementId}
                  </div>
                </>
              )}
          </>
        )
      }

      case PermitFieldType.Measure: {
        const measure = value as IMeasure
        return (
          <span className="text large">
            {measure?.value ?? ''} {measure?.units || ''}
          </span>
        )
      }

      case PermitFieldType.AdditionalDate: {
        const timestamp = value as number
        const formatted = projectDateStore?.getMonthDayAndYearToDisplay(
          new Date(timestamp),
        )
        return <span className="text large">{formatted || NO_VALUE}</span>
      }

      case PermitFieldType.RichText: {
        const richText = value as IRichText
        if (richText?.content?.length || richText?.attachments?.length) {
          return (
            <RichEditorParsed
              value={richText}
              isContentExpanded
              className="mt10 bb-light-grey"
            />
          )
        }
        return <div className="text large word-break-all">{NO_VALUE}</div>
      }

      case PermitFieldType.Activity:
      case PermitFieldType.PrimaryActivity:
      case PermitFieldType.LinkedWorkflow: {
        const workflowId = value as string
        return <span className="text large">{workflowId || NO_VALUE}</span>
      }

      // Simple text/string fields
      case PermitFieldType.Select:
      case PermitFieldType.InputText:
      case PermitFieldType.InputTextArea:
      case PermitFieldType.InputNumber:
      case PermitFieldType.Email:
      case PermitFieldType.PhoneNumber:
      case PermitFieldType.Time:
      default:
        return (
          <div className="text large word-break-all">
            {String(value) || NO_VALUE}
          </div>
        )
    }
  }
}

/**
 * Renders a checklist or question field in readonly mode.
 * Separated because it needs special handling for the checklist structure.
 */
interface IChecklistFieldProps extends IReadonlyFieldStores {
  values: IChecklistAnswerItem[]
  typeField: IPermitTypeField
}

@observer
export class ReadonlyChecklistField extends React.Component<IChecklistFieldProps> {
  public render() {
    const { values, typeField } = this.props
    const { checklist } = typeField

    if (!checklist?.list) {
      return <div className="text large word-break-all">{NO_VALUE}</div>
    }

    const answerMap = new Map(values.map(a => [a.checklistItemId, a]))
    const isQuestion = typeField.type === PermitFieldType.Question

    return (
      <div className="col inactive-element">
        {checklist.list.map(({ id, text, questionnaireType }, i) => {
          const answer = answerMap.get(id)
          const options = getQuestionnaireOptionsByType(
            questionnaireType as QuestionnaireType,
          )

          return (
            <div key={id} className="mb15 col">
              <div className="mb10">
                {!isQuestion && (
                  <span className="text large bold blue-highlight no-grow mr12">
                    {getBullet(checklist.listType, i + 1)}
                  </span>
                )}
                <span className="text large">{text}</span>
              </div>
              <div className="row no-grow x-center questionnaire-items">
                {options.map(({ value, title }) => (
                  <Toggle
                    key={title}
                    variant={
                      value === true
                        ? 'success'
                        : value === false
                          ? 'destructive'
                          : 'neutral'
                    }
                    pressed={answer?.answer === value}
                    readOnly={answer?.answer === value}
                    disabled={answer?.answer !== value}
                    className="mx5 ellipsis tw:grow"
                  >
                    {title}
                  </Toggle>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }
}

/**
 * Renders a complete field row with icon, caption, and values in readonly mode.
 * This component handles rendering multiple values for a field.
 */
@inject(
  'projectMembersStore',
  'companiesStore',
  'locationAttributesStore',
  'materialsStore',
  'projectDateStore',
)
@observer
export class ReadonlyFieldRow extends React.Component<IReadonlyFieldRowProps> {
  public render() {
    const { values, showHeader = true, typeField } = this.props

    // If no header, just return content (or null if empty)
    if (!showHeader) {
      return values?.length ? this.renderValuesContent(values) : null
    }

    // Use WorkflowFormFieldViewMode for layout
    return (
      <WorkflowFormFieldViewMode
        value={values}
        typeField={typeField}
        isTitleShown={true}
        isPaddingAdded={false}
        viewModeRenderer={this.renderValuesContent}
      />
    )
  }

  private renderValuesContent = (values: unknown[]): JSX.Element => {
    const {
      typeField,
      projectMembersStore,
      companiesStore,
      locationAttributesStore,
      materialsStore,
      projectDateStore,
    } = this.props

    if (!values?.length) {
      return <div className="text large word-break-all">{NO_VALUE}</div>
    }

    // Special handling for checklist/question fields
    if (
      typeField.type === PermitFieldType.Checklist ||
      typeField.type === PermitFieldType.Question
    ) {
      return (
        <ReadonlyChecklistField
          values={values as IChecklistAnswerItem[]}
          typeField={typeField}
        />
      )
    }

    // Render each value
    return (
      <div className="col">
        {values.map((value, i) => (
          <ReadonlyFieldValue
            key={i}
            value={value}
            typeField={typeField}
            projectMembersStore={projectMembersStore}
            companiesStore={companiesStore}
            locationAttributesStore={locationAttributesStore}
            materialsStore={materialsStore}
            projectDateStore={projectDateStore}
          />
        ))}
      </div>
    )
  }
}

// Field types that should not be displayed in readonly views
export const HIDDEN_FIELD_TYPES = new Set([
  PermitFieldType.Divider,
  PermitFieldType.Text,
  PermitFieldType.Instructions,
  PermitFieldType.Section,
  PermitFieldType.Subsection,
  PermitFieldType.InlineMedia,
])

/**
 * Checks if a field type should be hidden in readonly views
 */
export function isHiddenFieldType(type: PermitFieldType): boolean {
  return HIDDEN_FIELD_TYPES.has(type)
}
